import React from 'react';
import { Package, Users, Truck, MapPin, Award, Target } from 'lucide-react';
import Header from '@/components/layout/Header';
import FooterNew from '@/components/layout/FooterNew';
import { Card, CardContent } from '@/components/ui/card';

const AboutPage = () => {
  const values = [
    {
      icon: Award,
      title: 'Excellence',
      description: 'Nous nous engageons à fournir un service de qualité supérieure à tous nos clients.'
    },
    {
      icon: Users,
      title: 'Confiance',
      description: 'La confiance de nos clients et partenaires est au cœur de notre activité.'
    },
    {
      icon: Target,
      title: 'Innovation',
      description: 'Nous adoptons les dernières technologies pour améliorer constamment nos services.'
    }
  ];

  const stats = [
    { label: 'Livraisons réussies', value: '10,000+' },
    { label: 'Clients satisfaits', value: '500+' },
    { label: 'Villes couvertes', value: '3' },
    { label: 'Livreurs actifs', value: '50+' }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <Header />
      
      <main className="flex-1">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold gradient-text mb-6">
                À propos de Coli Noki
              </h1>
              <p className="text-lg text-gray-700 leading-relaxed">
                Votre partenaire de confiance pour la gestion de livraisons en Afrique Centrale.
                Nous connectons vendeurs, livreurs et clients pour une expérience de livraison optimale.
              </p>
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="grid md:grid-cols-2 gap-8 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 mb-4">
                    Notre Mission
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Coli Noki est né de la volonté de simplifier et professionnaliser 
                    le secteur de la livraison dans les grandes villes d'Afrique Centrale.
                  </p>
                  <p className="text-gray-700 leading-relaxed">
                    Nous offrons une plateforme moderne qui permet aux vendeurs de gérer 
                    leurs livraisons en toute transparence, aux livreurs de maximiser leurs 
                    revenus, et aux clients de suivre leurs colis en temps réel.
                  </p>
                </div>
                <div className="bg-gradient-to-br from-red-100 to-cyan-100 rounded-2xl p-8">
                  <Package className="w-16 h-16 text-red-600 mb-4" />
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">
                    Livraisons Rapides & Sécurisées
                  </h3>
                  <p className="text-gray-700">
                    Nous garantissons la sécurité de vos colis et la rapidité de livraison.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Values Section */}
        <section className="bg-gray-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                Nos Valeurs
              </h2>
              <div className="grid md:grid-cols-3 gap-8">
                {values.map((value, index) => {
                  const Icon = value.icon;
                  return (
                    <Card key={index} className="text-center hover:shadow-lg transition-shadow">
                      <CardContent className="pt-8 pb-6">
                        <div className="w-16 h-16 bg-gradient-to-br from-red-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-4">
                          <Icon className="w-8 h-8 text-white" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {value.title}
                        </h3>
                        <p className="text-gray-600">
                          {value.description}
                        </p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">
                Coli Noki en Chiffres
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <p className="text-4xl font-bold gradient-text mb-2">
                      {stat.value}
                    </p>
                    <p className="text-gray-600">
                      {stat.label}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Coverage Section */}
        <section className="bg-gradient-to-br from-red-50 to-cyan-50 py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <MapPin className="w-16 h-16 text-red-600 mx-auto mb-6" />
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Zone de Couverture
              </h2>
              <p className="text-lg text-gray-700 mb-8">
                Nous sommes présents dans les principales villes d'Afrique Centrale
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                  <p className="font-semibold text-gray-900">Brazzaville (Congo)</p>
                </div>
                <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                  <p className="font-semibold text-gray-900">Pointe-Noire (Congo)</p>
                </div>
                <div className="bg-white rounded-lg px-6 py-3 shadow-md">
                  <p className="font-semibold text-gray-900">Kinshasa (RDC)</p>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <FooterNew />
    </div>
  );
};

export default AboutPage;